// Viewport culling, shared by every layer that draws map objects
// (ui-map-interface.md §10.7; R186 built it for the sites, R192 gave it to
// the fleets, the wake and the rounds in flight).
//
// The rule is one rule: a thing outside the visible world rect, plus a
// margin, does no per-frame work and goes to the renderer invisible. The
// rect and the margin therefore live in one file, so no layer can pick a
// different margin and no layer can grow a second copy of the type.
//
// Nothing here allocates. A layer grows the rect one time for each tick and
// then compares plain numbers.

/** The visible world rect in world px, as `main.ts` measures it once a frame. */
export interface ViewRect { x0: number; y0: number; x1: number; y1: number }

/**
 * How far past the screen edge a map object still draws, in world px (R186).
 *
 * An object is one point with a mark around it, and the widest mark is the
 * own hold's ground plate at r27 plus the beacon above it. 80 px covers that
 * mark and the sonar glow the object sits in, so a mark that overlaps the
 * screen edge is never cut off. The figure lives in
 * data/presentation/presentation.toml (R312); nothing else depends on it. A
 * larger mark, or a zoom below 0.5, needs the figure again.
 */
export { CULL_MARGIN_PX } from '../gen/presentation';

/**
 * Is this point outside the **already grown** bounds? Four number compares
 * and no allocation, which is the whole reason the culling is cheap.
 *
 * The caller grows the rect one time for each tick — `view.x0 -
 * CULL_MARGIN_PX` and so on — and then calls this for each object.
 */
export function outside(
  x: number, y: number, x0: number, y0: number, x1: number, y1: number,
): boolean {
  return x < x0 || x > x1 || y < y0 || y > y1;
}
